// src/pages/Services.jsx
import React from "react";
import {
  FaShip,
  FaPlane,
  FaTruck,
  FaFileContract,
  FaWarehouse,
  FaNetworkWired,
  FaCheck,
  FaClock,
  FaPhone,
  FaEnvelope,
  FaArrowRight,
  FaGlobe,
  FaShieldAlt,
  FaHeadset,
} from "react-icons/fa";
import "./Services.css";

const Services = () => {
  const services = [
    {
      icon: <FaShip />,
      title: "Sea Freight Shipping",
      description:
        "Cost-efficient FCL/LCL with dependable ETAs and proactive milestone updates. EXW/FOB coordination, Port handling at Mombasa & ICD.",
      features: [
        "Full Container Load (FCL) & LCL",
        "Consolidation & deconsolidation",
        "Project & oversized cargo handling",
        "Ports: Mombasa, Dar es Salaam, Durban",
        "EXW/FOB coordination worldwide",
        "Specialized equipment available",
      ],
      transitTime: "25–40 days",
      coverage: "Global Coverage",
      badge: "Most Popular",
    },
    {
      icon: <FaPlane />,
      title: "Air Freight Services",
      description:
        "Priority uplift via JKIA with tight transit times and compliant documentation. Airline bookings & AWB, Urgent spares & perishables.",
      features: [
        "Priority uplift from JKIA",
        "Temperature-controlled transport",
        "Charter services available",
        "Perishables & pharmaceuticals",
        "Urgent spares & valuables",
        "Air Waybill processing",
      ],
      transitTime: "1–5 days",
      coverage: "JKIA & Wilson Airports",
      badge: "Fastest",
    },
    {
      icon: <FaTruck />,
      title: "Road Transport",
      description:
        "Reliable trucking for port-to-ICD, last-mile and up-country deliveries. Container & loose cargo handling, Secure, GPS-tracked movements.",
      features: [
        "Port to ICD transfers",
        "Last-mile & upcountry delivery",
        "Cross-border to Uganda, Rwanda, Tanzania",
        "GPS-tracked modern fleet",
        "Project cargo specialists",
        "Container & loose cargo",
      ],
      transitTime: "Flexible Scheduling",
      coverage: "East Africa Network",
      badge: "GPS Tracked",
    },
    {
      icon: <FaFileContract />,
      title: "Customs Clearance & Brokerage",
      description:
        "Licensed KRA processes that reduce dwell time and prevent penalties. IDF, HS classification, permits (KEBS/PPB), Inspections & audit-ready documentation.",
      features: [
        "IDF & permit applications",
        "HS code & tariff advisory",
        "KEBS, PPB, KEPHIS coordination",
        "Audit-ready documentation",
        "Inspections management",
        "100% KRA compliance",
      ],
      transitTime: "1–3 days clearance",
      coverage: "Nairobi ICD & Mombasa",
      badge: "Licensed",
    },
    {
      icon: <FaWarehouse />,
      title: "Warehousing & Distribution",
      description:
        "Secure storage with pick & pack and nationwide distribution. Inventory control & KPI reporting, Retail, B2B & e-commerce fulfilment.",
      features: [
        "Bonded & general storage",
        "Pick & pack fulfillment",
        "24/7 CCTV & access control",
        "E-commerce & B2B ready",
        "Inventory KPI reporting",
        "Nationwide distribution",
      ],
      transitTime: "Same Day Processing",
      coverage: "50,000+ sq ft Capacity",
      badge: "Secure",
    },
    {
      icon: <FaNetworkWired />,
      title: "Supply Chain Management",
      description:
        "PO-to-POD coordination, documentation and cost control for predictable delivery. Vendor & milestone management, Transparent, sales-ready pricing.",
      features: [
        "PO-to-POD coordination",
        "Real-time tracking portal",
        "KPI dashboards & reporting",
        "Vendor & milestone management",
        "ERP integration ready",
        "End-to-end visibility",
      ],
      transitTime: "Real-time Tracking",
      coverage: "Full Visibility",
      badge: "Integrated",
    },
  ];

  const processSteps = [
    {
      icon: <FaHeadset />,
      title: "Consultation & Quote",
      description:
        "We understand your needs and provide a transparent, all-inclusive quote.",
      color: "#3B82F6",
    },
    {
      icon: <FaFileContract />,
      title: "Documentation",
      description:
        "All required paperwork handled with precision and full compliance.",
      color: "#10B981",
    },
    {
      icon: <FaGlobe />,
      title: "Shipping & Tracking",
      description: "Real-time updates every step of your cargo's journey.",
      color: "#F59E0B",
    },
    {
      icon: <FaShieldAlt />,
      title: "Customs Clearance",
      description: "Fast, compliant clearance by licensed experts.",
      color: "#8B5CF6",
    },
    {
      icon: <FaCheck />,
      title: "Final Delivery",
      description:
        "Secure delivery with proof of receipt and full documentation.",
      color: "#EC4899",
    },
  ];

  const stats = [
    { number: "99%", label: "On-Time Delivery" },
    { number: "24/7", label: "Customer Support" },
    { number: "50+", label: "Expert Team" },
    { number: "100%", label: "KRA Compliant" },
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">Premium Logistics Services</div>
            <h1>Our Comprehensive Logistics Solutions</h1>
            <p>
              End-to-end freight forwarding, customs clearance, warehousing, and
              supply chain solutions — trusted across East Africa and beyond.
            </p>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="all-services">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Services</div>
            <h2>All Services, One Trusted Partner</h2>
            <p>
              Comprehensive logistics solutions tailored to your business needs
              across East Africa
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                {service.badge && (
                  <div className="service-badge">{service.badge}</div>
                )}
                <div className="service-icon-wrapper">
                  <div className="service-icon">{service.icon}</div>
                </div>
                <h3>{service.title}</h3>
                <p className="service-desc">{service.description}</p>

                <div className="service-features">
                  <h4>Key Features:</h4>
                  <ul>
                    {service.features.map((feature, i) => (
                      <li key={i}>
                        <FaCheck className="check-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-meta">
                  <div className="transit-info">
                    <FaClock className="meta-icon" />
                    <div>
                      <div className="meta-label">Transit Time</div>
                      <div className="meta-value">{service.transitTime}</div>
                    </div>
                  </div>
                  <div className="coverage-info">
                    <FaGlobe className="meta-icon" />
                    <div>
                      <div className="meta-label">Coverage</div>
                      <div className="meta-value">{service.coverage}</div>
                    </div>
                  </div>
                </div>

                <button className="service-cta">
                  Learn More <FaArrowRight />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">Our Process</div>
            <h2>Streamlined Logistics Workflow</h2>
            <p>
              From consultation to final delivery - our proven process ensures
              efficiency and reliability
            </p>
          </div>

          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div
                  className="step-icon"
                  style={{ backgroundColor: step.color }}
                >
                  {step.icon}
                </div>
                <div className="step-content">
                  <div className="step-number">Step {index + 1}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="step-connector">
                    <div className="connector-line"></div>
                    <FaArrowRight className="connector-arrow" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>Ready to Streamline Your Logistics?</h2>
              <p>
                Get a free consultation and customized quote today. Our expert
                team is ready to handle your shipping needs with precision and
                care.
              </p>
              <div className="cta-features">
                <div className="feature">
                  <FaCheck className="feature-check" />
                  <span>Transparent Pricing</span>
                </div>
                <div className="feature">
                  <FaCheck className="feature-check" />
                  <span>24/7 Support</span>
                </div>
                <div className="feature">
                  <FaCheck className="feature-check" />
                  <span>Real-time Tracking</span>
                </div>
              </div>
            </div>
            <div className="cta-buttons">
              <a href="tel:+254723335043" className="btn btn-primary">
                <FaPhone className="btn-icon" />
                <div>
                  <div className="btn-main">Call Now</div>
                  <div className="btn-sub">+254 723 335 043</div>
                </div>
              </a>
              <a
                href="mailto:info@falmouthglobal.co.ke"
                className="btn btn-secondary"
              >
                <FaEnvelope className="btn-icon" />
                <div>
                  <div className="btn-main">Email Us</div>
                  <div className="btn-sub">Quick Response</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
